import { OverflowList } from '@theme/overflow-list';
import VariantPicker from '@theme/variant-picker';
import { ProductComponent } from '@theme/view-event-elements';
import { debounce, isDesktopBreakpoint, mediaQueryLarge, yieldToMainThread } from '@theme/utilities';
import { SlideshowSelectEvent } from '@theme/events';
import { morph } from '@theme/morph';
import { StandardEvents, ProductSelectEvent } from '@shopify/events';

/**
 * @typedef {object} ProductCardLinkRefs
 * @property {HTMLElement} [cardGallery] - The card gallery element.
 * @property {HTMLImageElement[]} [imagesToTransition] - The images to transition.
 */

/**
 * A custom element for product links with images for transitions to PDP.
 * This is a base class that is extended by ProductCard.
 * Used directly by resource-card.liquid for non-product-card scenarios.
 * Extends ProductComponent to automatically emit product:view events when visible.
 *
 * @template {ProductCardLinkRefs} [T=ProductCardLinkRefs]
 * @extends {ProductComponent<T>}
 */
export class ProductCardLink extends ProductComponent {
  get productTransitionEnabled() {
    return this.getAttribute('data-product-transition') === 'true';
  }

  get featuredMediaUrl() {
    return this.getAttribute('data-featured-media-url');
  }

  /**
   * Handles the click event for view transitions.
   * @param {Event} event
   */
  handleViewTransition(event) {
    // If the event has been prevented, don't do anything, another component is handling the click
    if (event.defaultPrevented) return;

    // If the event was on an interactive element, don't do anything, this is not a navigation
    if (event.target instanceof Element) {
      const interactiveElement = event.target.closest('button, input, label, select, [tabindex="1"]');
      if (interactiveElement) return;
    }

    if (!this.productTransitionEnabled) return;

    const { cardGallery } = this.refs;
    if (!cardGallery || !cardGallery.hasAttribute('data-view-transition-to-main-product')) return;

    // Check on the current active image, whether it's a product card image or a resource card image
    const { imagesToTransition } = this.refs;
    const activeImage =
      imagesToTransition?.find(
        (/** @type {HTMLImageElement} */ image) =>
          image.closest('slideshow-slide')?.getAttribute('aria-hidden') === 'false'
      ) || imagesToTransition?.[imagesToTransition.length - 1];

    if (activeImage instanceof HTMLImageElement) this.#setImageSrcset(activeImage);

    cardGallery.setAttribute('data-view-transition-type', 'product-image-transition');
    cardGallery.setAttribute('data-view-transition-triggered', 'true');
  }

  /**
   * Sets the srcset for the image
   * @param {HTMLImageElement} image
   */
  #setImageSrcset(image) {
    if (!this.featuredMediaUrl) return;

    const currentImageUrl = new URL(image.currentSrc);

    // Deliberately not using origin, as it includes the protocol, which is usually skipped for featured media
    const currentImageRawUrl = currentImageUrl.host + currentImageUrl.pathname;

    if (!this.featuredMediaUrl.includes(currentImageRawUrl)) {
      const imageFade = image.animate([{ opacity: 0.8 }, { opacity: 1 }], {
        duration: 125,
        easing: 'ease-in-out',
      });

      imageFade.onfinish = () => {
        image.srcset = this.featuredMediaUrl ?? '';
      };
    }
  }
}

if (!customElements.get('product-card-link')) {
  customElements.define('product-card-link', ProductCardLink);
}

/**
 * A custom element that displays a product card.
 * Extends ProductCardLink to inherit view transition functionality.
 *
 * @typedef {object} ProductCardRefs
 * @property {HTMLAnchorElement} productCardLink - The product card link element.
 * @property {import('slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {import('quick-add').QuickAddComponent} [quickAdd] - The quick add component.
 * @property {HTMLElement} [cardGallery] - The card gallery component.
 * @property {HTMLElement} [swatchOverlay] - The swatch color overlay element.
 * @property {HTMLImageElement[]} [imagesToTransition] - The images to transition.
 * @extends {ProductCardLink<ProductCardRefs>}
 */
export class ProductCard extends ProductCardLink {
  requiredRefs = ['productCardLink'];

  get productPageUrl() {
    const link = this.refs.productCardLink;
    if (!link.getAttribute('href')) return '';

    const url = new URL(link.href);
    const variantOverride = this.#getProductPageVariantOverride();

    if (variantOverride?.action === 'set') {
      url.searchParams.set('variant', variantOverride.variantId);
    } else if (variantOverride?.action === 'remove') {
      url.searchParams.delete('variant');
    }

    return url.toString();
  }

  /**
   * Gets the user-selected variant ID that still needs to be synced from product-card state.
   * @returns {string | null} Variant ID or null.
   */
  getSelectedVariantId() {
    const variantOverride = this.#getProductPageVariantOverride();
    return variantOverride?.action === 'set' ? variantOverride.variantId : null;
  }

  /**
   * Gets the user-selected variant override for product-page URLs.
   * @returns {{ action: 'set', variantId: string } | { action: 'remove' } | null}
   */
  #getProductPageVariantOverride() {
    const link = this.refs.productCardLink;
    if (link.getAttribute('href')) {
      const url = new URL(link.href);
      if (url.searchParams.has('variant')) return null;
    }

    const variantId = this.#getCheckedVariantId();
    if (variantId === undefined) return null;

    return variantId ? { action: 'set', variantId } : { action: 'remove' };
  }

  /** @returns {string | undefined} */
  #getCheckedVariantId() {
    const checkedInput = /** @type {HTMLInputElement | null} */ (
      this.querySelector('input[type="radio"]:checked[data-variant-id]')
    );

    return checkedInput?.dataset.variantId;
  }

  /**
   * Gets the product card link element
   * @returns {HTMLAnchorElement | null} The product card link or null
   */
  getProductCardLink() {
    return this.refs.productCardLink || null;
  }

  #fetchProductPageHandler = () => {
    this.refs.quickAdd?.fetchProductPage(this.productPageUrl);
  };

  /**
   * GOAT (perf): true when clicking this card opens the GOAT Quick Shop modal rather than Horizon's
   * fallback quick-add modal.
   *
   * `QuickAddComponent.handleQuickShop` short-circuits to `QuickShopDialog.trigger()` whenever the
   * shell exists AND the card carries a product handle, and that path fetches a single
   * `?section_id=quick-shop-product` render. Horizon's warm — a fetch of the ENTIRE product page,
   * DOMParsed and then thrown away, because only `handleClick` populates `#cachedContent` — is
   * therefore pure waste on those cards, once per hover and again on every swatch click.
   *
   * Both conditions are required, because `handleQuickShop` also falls through to `handleClick`
   * when the handle is missing. Cards rendered without the shell (recommendations, featured
   * products, hotspots) keep the upstream warm untouched.
   * @returns {boolean}
   */
  get #quickShopOwnsCard() {
    return Boolean(document.getElementById('quick-shop-modal') && this.refs.quickAdd?.dataset.productHandle);
  }

  /**
   * Navigates to a URL link. Respects modifier keys for opening in new tab/window.
   * @param {Event} event - The event that triggered the navigation.
   * @param {URL} url - The URL to navigate to.
   */
  #navigateToURL = (event, url) => {
    // Check for modifier keys that should open in new tab/window (only for mouse events)
    const shouldOpenInNewTab =
      event instanceof MouseEvent && (event.metaKey || event.ctrlKey || event.shiftKey || event.button === 1);

    if (shouldOpenInNewTab) {
      event.preventDefault();
      window.open(url.href, '_blank');
      return;
    } else {
      window.location.href = url.href;
    }
  };

  connectedCallback() {
    super.connectedCallback();

    const link = this.refs.productCardLink;
    if (!(link instanceof HTMLAnchorElement)) throw new Error('Product card link not found');

    this.#handleQuickAdd();

    this.addEventListener(StandardEvents.productSelect, this.#handleProductSelect);
    this.addEventListener(SlideshowSelectEvent.eventName, this.#handleSlideshowSelect);
    mediaQueryLarge.addEventListener('change', this.#handleQuickAdd);

    this.addEventListener('click', this.navigateToProduct);

    // Case 3: a re-click on the already-checked swatch emits no `change`.
    this.addEventListener('click', this.#syncFromSwatchInteraction);

    // Case 2: bfcache / form restoration on back-forward navigation restores `checked` silently.
    // Not gated on `event.persisted` — Chrome also restores form state without a bfcache hit.
    window.addEventListener('pageshow', this.#syncToCheckedSwatch);

    // Preload the next image on the slideshow to avoid white flashes on previewImage
    setTimeout(() => {
      if (this.refs.slideshow?.isNested) {
        this.#preloadNextPreviewImage();
      }

      // Case 1: the server-rendered href and the checked swatch can disagree. Deferred so the
      // swatch picker and slideshow children have upgraded and their refs resolve.
      this.#syncToCheckedSwatch();
    });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('click', this.navigateToProduct);
    this.removeEventListener('click', this.#syncFromSwatchInteraction);
    window.removeEventListener('pageshow', this.#syncToCheckedSwatch);
    // GOAT: connectedCallback registers this on the shared media query but upstream never removed
    // it, so every card replaced by a facet re-render or destroyed during infinite scroll left a
    // listener holding a reference to the detached element.
    mediaQueryLarge.removeEventListener('change', this.#handleQuickAdd);
  }

  #preloadNextPreviewImage() {
    const currentSlide = this.refs.slideshow?.slides?.[this.refs.slideshow?.current];
    currentSlide?.nextElementSibling?.querySelector('img[loading="lazy"]')?.removeAttribute('loading');
  }

  /**
   * Handles the quick add event.
   */
  #handleQuickAdd = () => {
    this.removeEventListener('pointerenter', this.#fetchProductPageHandler);
    this.removeEventListener('focusin', this.#fetchProductPageHandler);

    // GOAT (perf): see `#quickShopOwnsCard` — on these cards the warm is discarded, so never bind.
    if (this.#quickShopOwnsCard) return;

    if (isDesktopBreakpoint()) {
      this.addEventListener('pointerenter', this.#fetchProductPageHandler);
      this.addEventListener('focusin', this.#fetchProductPageHandler);
    }
  };

  /**
   * Handles the product select event (variant selected and updated).
   * @param {ProductSelectEvent} event - The product select event.
   */
  #handleProductSelect = (event) => {
    // Update variant picker when variant:selected event fires
    const { optionValueId, variantId, connectedProductUrl } = event.detail ?? {};
    if (optionValueId && event.target !== this.variantPicker) {
      this.variantPicker?.updateSelectedOption(optionValueId);
    }

    // Empty string removes ?variant=.
    if (typeof variantId === 'string') {
      this.applyVariantToLinks(variantId, typeof connectedProductUrl === 'string' ? connectedProductUrl : undefined);
    }

    // Wait for variant:update data via promise
    event.promise
      .then(({ detail }) => {
        if (!detail?.html) return;

        const { html } = detail;

        // Update price, availability, and URL based on new variant
        this.updatePrice(html);
        this.#isUnavailableVariantSelected(html);
        this.#updateProductUrl(html, typeof variantId === 'string' ? variantId : undefined);
        // GOAT (perf): same gate as the hover warm — on Quick Shop cards this queued a whole
        // product-page render per swatch click and discarded the result.
        if (!this.#quickShopOwnsCard) this.refs.quickAdd?.fetchProductPage(this.productPageUrl);

        if (event.target !== this.variantPicker) {
          this.variantPicker?.updateVariantPicker(html);
        }

        this.#updateVariantImages();
        this.#previousSlideIndex = null;

        // Sync the swatch overlay color with the newly selected variant. Horizon 4.0 moved the
        // DOM update into this promise, so the overlay has to be refreshed here rather than
        // synchronously in the old #handleVariantUpdate handler.
        this.#updateSwatchOverlay();

        // Remove attribute after re-rendering since a variant selection has been made
        this.removeAttribute('data-no-swatch-selected');

        // Force overflow list to reflow after variant update
        this.#updateOverflowList();
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[product-card] Event promise rejected:', error);
      });
  };

  /**
   * Forces the overflow list to recalculate by dispatching a reflow event.
   * This ensures the overflow counter displays correctly after variant updates.
   */
  #updateOverflowList() {
    // Find the overflow list in the variant picker
    const overflowList = this.querySelector('swatches-variant-picker-component overflow-list');
    if (!overflowList) return;

    // After a variant change the swatch row is re-rendered via a section-render morph. The
    // overflow measurement (driven by the overflow-list's own MutationObserver) can run before
    // the new layout settles and wrongly push EVERY swatch into the overflow slot — making the
    // whole swatch row disappear and leaving only the "+N" link (which then navigates to the PDP
    // on a second click). Re-run the reflow once layout has settled (double rAF), then guarantee
    // the row is never left empty.
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        overflowList.dispatchEvent(new CustomEvent('reflow', { bubbles: true, detail: {} }));

        requestAnimationFrame(() => {
          const swatches = overflowList.querySelectorAll(':scope > li:not([slot="more"])');
          const overflowed = overflowList.querySelectorAll(':scope > li[slot="overflow"]');
          if (swatches.length > 0 && overflowed.length === swatches.length && typeof overflowList.showAll === 'function') {
            // Reflow collapsed every swatch into overflow — show them all rather than nothing.
            overflowList.showAll();
          }
        });
      });
    });
  }

  /**
   * Updates the DOM with a new price.
   * @param {Document} html - The parsed HTML document with updated variant data.
   */
  updatePrice(html) {
    const priceContainer = this.querySelectorAll(`product-price [ref='priceContainer']`)[1];
    const newPriceElement = html.querySelector(`product-price [ref='priceContainer']`);

    if (newPriceElement && priceContainer) {
      morph(priceContainer, newPriceElement);
    }
  }

  /**
   * Updates the product URL based on the variant update.
   * @param {Document} html - The parsed HTML document with updated variant data.
   * @param {string} [intendedVariantId] - User-intended variant.
   */
  #updateProductUrl(html, intendedVariantId) {
    const responseProductCard = html.querySelector('product-card');
    const anchorElement = responseProductCard?.querySelector('a');
    const featuredMediaUrl = responseProductCard?.getAttribute('data-featured-media-url');

    // Update the featured media URL for view transitions (inherited from ProductCardLink)
    if (featuredMediaUrl) {
      this.setAttribute('data-featured-media-url', featuredMediaUrl);
    }

    if (anchorElement instanceof HTMLAnchorElement) {
      // If the href is empty, don't update the product URL eg: unavailable variant
      if (anchorElement.getAttribute('href')?.trim() === '') return;

      const responseUrl = new URL(anchorElement.href);
      this.#updateLinks({
        pathname: responseUrl.pathname,
        variantId: intendedVariantId ?? responseUrl.searchParams.get('variant'),
      });
    }
  }

  /**
   * Applies the given variant ID to the variant param of every link in the card.
   * Public so the components, namely swatches component can call it.
   * @param {string | null} variantId - The variant ID to set, or null to remove it.
   * @param {string} [productUrl] - Product URL path to adopt.
   */
  applyVariantToLinks(variantId, productUrl) {
    let pathname;
    if (productUrl) {
      try {
        pathname = new URL(productUrl, window.location.origin).pathname;
      } catch (error) {
        console.warn('[product-card] Invalid product URL:', productUrl, error);
        return;
      }
    }

    this.#updateLinks({ pathname, variantId });
  }

  /**
   * Reconciles everything derived from the selected colour — the card / gallery / title hrefs, the
   * swatch row's "+N" links, the gallery slide and the swatch overlay — with the swatch that is
   * actually `:checked`.
   *
   * All of that was previously written ONLY by the swatch `change` handler. Three situations
   * produce a checked swatch with no `change` event, and each one shipped the server default:
   *
   *  1. Server render. The card link is `{{ product.url }}`, whose `variant` param is Shopify's
   *     algorithmic pick, while the checked swatch is chosen independently in
   *     snippets/variant-swatches.liquid. Measured on the best-sellers PLP: 6 of 13 cards
   *     disagreed — three pointed at a different colour, three carried no `variant` param at all,
   *     so the PDP fell back to first-available.
   *  2. Back / forward. The browser restores the radio's checked PROPERTY without firing `change`
   *     (the `checked` ATTRIBUTE stays on the server's default), so the swatch came back correct
   *     while the href and image reverted.
   *  3. Re-clicking the already-checked swatch, which by spec emits no `change`.
   *
   * Deriving from the checked input closes all three with one code path. Fixing (1) in Liquid
   * instead would mean duplicating the "which swatch is checked" resolution into the link markup —
   * the very drift that causes this bug.
   */
  #syncToCheckedSwatch = () => {
    const selected = this.variantPicker?.selectedOption;
    if (!(selected instanceof HTMLInputElement)) return;

    const variantId = selected.dataset.variantId || selected.dataset.firstAvailableOrFirstVariantId;
    if (!variantId) return;

    const link = this.refs.productCardLink;
    const currentVariantId =
      link instanceof HTMLAnchorElement && link.href ? new URL(link.href).searchParams.get('variant') : null;

    // Already consistent — do not touch the DOM, so a normal `change` selection is never
    // second-guessed and the gallery is not re-driven on every click.
    if (currentVariantId === variantId) return;

    this.#updateLinks({ variantId });
    this.#updateVariantImages();
    this.#updateSwatchOverlay();
  };

  /**
   * Runs the reconcile after the current task, so it sees the radio's settled checked state.
   * A label click toggles the radio during dispatch, so reading it synchronously from a bubbled
   * click can catch the previous value.
   * @param {Event} event
   */
  #syncFromSwatchInteraction = (event) => {
    if (!(event.target instanceof Element)) return;
    if (!event.target.closest('swatches-variant-picker-component')) return;
    setTimeout(this.#syncToCheckedSwatch);
  };

  /**
   * Public entry for other components to point the card's links at a variant.
   *
   * The quick-shop overlay lists EVERY colour, but the card renders at most five swatch inputs —
   * so for colours behind "+N" there is no radio to click and no `change` event, and the card's
   * links kept the old variant: choose a deep colour in the overlay, click the card image or
   * title, land on the wrong variant. The overlay resolves the variant itself and hands it here.
   * @param {string} variantId
   */
  updateLinksToVariant(variantId) {
    if (!variantId) return;
    this.#updateLinks({ variantId });
  }

  /**
   * Updates every link in the card (overlay, gallery, title, and the swatch row's "+N") in place,
   * preserving each link's existing query params so tracking params stay in sync.
   * Always sets (or removes) the `variant` param; optionally adopts a new pathname (for combined-listing child navigation).
   * @param {object} options
   * @param {string} [options.pathname] - New pathname to adopt (combined-listing child navigation). Omit to keep each link's current path.
   * @param {string | null} [options.variantId] - Variant ID to set, or null/undefined to remove it.
   */
  #updateLinks({ pathname, variantId }) {
    const { productCardLink, productTitleLink, cardGalleryLink } = this.refs;
    // The "+N" overflow links go to the PDP too, so they have to follow the selected colour —
    // production does the same in `inputChangeEventListeners` (product-card.js:139-144 of
    // goat-shopify-redesign). They are not `ref`s: there are up to two of them, one per breakpoint.
    const swatchMoreLinks = Array.from(this.querySelectorAll('.card-swatch-more'));
    for (const linkEl of [productCardLink, cardGalleryLink, productTitleLink, ...swatchMoreLinks]) {
      if (!(linkEl instanceof HTMLAnchorElement) || !linkEl.href) continue;
      const url = new URL(linkEl.href);
      if (pathname) url.pathname = pathname;
      if (variantId) url.searchParams.set('variant', variantId);
      else url.searchParams.delete('variant');
      linkEl.href = url.toString();
    }
  }

  /**
   * Checks if an unavailable variant is selected.
   * @param {Document} html - The parsed HTML document with updated variant data.
   */
  #isUnavailableVariantSelected(html) {
    const allVariants = /** @type {NodeListOf<HTMLInputElement>} */ (html.querySelectorAll('input:checked'));

    for (const variant of allVariants) {
      this.#toggleAddToCartButton(variant.dataset.optionAvailable === 'true');
    }
  }

  /**
   * Toggles the add to cart button state.
   * @param {boolean} enable - Whether to enable or disable the button.
   */
  #toggleAddToCartButton(enable) {
    const addToCartButton = this.querySelector('.add-to-cart__button button');

    if (addToCartButton instanceof HTMLButtonElement) {
      addToCartButton.disabled = !enable;
    }
  }

  /**
   * Hide the variant images that are not for the selected variant.
   */
  #updateVariantImages() {
    const { slideshow } = this.refs;
    if (!this.variantPicker?.selectedOption) {
      return;
    }

    const selectedImageId = this.variantPicker?.selectedOption.dataset.optionMediaId;

    if (slideshow && selectedImageId) {
      if (!slideshow.refs) {
        customElements.whenDefined(slideshow.localName).then(() => this.#updateVariantImages());
        return;
      }

      // Colour carousel: every slide stays in the flow and we scroll between them. This runs after
      // the section re-render, by which point animateToVariantImage() has usually already landed on
      // the slide (select() no-ops when it is current) — so it doubles as a self-heal for a click
      // that arrived while a previous slide transition still held the slideshow.
      if (this.animateToVariantImage(selectedImageId)) return;

      const { slides = [] } = slideshow.refs;

      // Without a matching slide, the loop below would hide every variant image and leave the card
      // blank, so bail rather than emptying the gallery.
      if (!slides.some((slide) => slide.getAttribute('slide-id') === selectedImageId)) return;

      for (const slide of slides) {
        if (slide.getAttribute('variant-image') == null) continue;

        slide.hidden = slide.getAttribute('slide-id') !== selectedImageId;
      }

      slideshow.select({ id: selectedImageId }, undefined, { animate: false });
    }
  }

  /**
   * Gets all variant inputs.
   * @returns {NodeListOf<HTMLInputElement>} All variant input elements.
   */
  get allVariants() {
    return this.querySelectorAll('input[data-variant-id]');
  }

  /**
   * Gets the variant picker component.
   * @returns {VariantPicker | null} The variant picker component.
   */
  get variantPicker() {
    return this.querySelector('swatches-variant-picker-component');
  }
  /** @type {number | null} */
  #previousSlideIndex = null;

  /**
   * Handles the slideshow select event.
   * @param {SlideshowSelectEvent} event - The slideshow select event.
   */
  #handleSlideshowSelect = (event) => {
    if (event.detail.userInitiated) {
      this.#previousSlideIndex = event.detail.index;
    }

    if (event.target === this.refs.slideshow) {
      this.#updateGalleryScrollbar(event.detail.index);
    }
  };

  /**
   * Whether the card gallery is production's one-slide-per-colour carousel
   * (see the GOAT comment in snippets/card-gallery.liquid).
   * @returns {boolean}
   */
  get #hasColorSlides() {
    return this.refs.slideshow?.getAttribute('data-color-slides') === 'true';
  }

  /**
   * Moves the colour-carousel scroll indicator to a slide index. Geometry lives in CSS; this only
   * writes the index, which the thumb's transform reads and transitions.
   * @param {number} index - The slide index.
   */
  #updateGalleryScrollbar(index) {
    const { galleryScrollbar } = this.refs;

    if (!galleryScrollbar || typeof index !== 'number' || Number.isNaN(index)) return;

    galleryScrollbar.style.setProperty('--gallery-slide-index', String(index));
  }

  /**
   * Slides the colour carousel to a variant's image, the way production's `swiper.slideTo` does.
   * Called synchronously from the swatch's change handler so the slide starts on click rather
   * than after the section re-render round-trip.
   * @param {string | undefined} mediaId - The media id of the target slide (`data-option-media-id`).
   * @returns {boolean} Whether the slide was handled here; false means the caller should fall back.
   */
  animateToVariantImage(mediaId) {
    const { slideshow } = this.refs;

    if (!slideshow || !mediaId || !this.#hasColorSlides) return false;

    // A disabled slideshow (nested in an outer carousel) cannot scroll, so leave it to the
    // legacy show/hide path — otherwise the card image would never change there.
    if (slideshow.disabled) return false;

    const id = String(mediaId);
    const hasSlide = slideshow.refs?.slides?.some(
      (/** @type {Element} */ slide) => slide.getAttribute('slide-id') === id
    );

    // If the swatch's media id does not match any slide, the select() below would silently do
    // nothing — report it as unhandled so the caller can fall back instead.
    if (!hasSlide) return false;

    slideshow.select({ id });

    return true;
  }

  /**
   * Previews a variant.
   * @param {string} id - The id of the variant to preview.
   */
  previewVariant(id) {
    const { slideshow, swatchOverlay } = this.refs;

    if (!slideshow) return;

    this.resetVariant.cancel();

    if (swatchOverlay) {
      const label = this.querySelector(`label[data-media-id="${id}"]`);
      const swatchColor = label?.dataset.swatchColor;

      if (swatchColor) {
        swatchOverlay.style.background = swatchColor;
      }
    }

    slideshow.select({ id }, undefined, { animate: false });
  }

  /**
   * Previews the next image.
   * @param {PointerEvent} event - The pointer event.
   */
  previewImage(event) {
    if (event.pointerType !== 'mouse') return;

    const { slideshow } = this.refs;

    if (!slideshow) return;

    this.resetVariant.cancel();

    if (this.#previousSlideIndex != null && this.#previousSlideIndex > 0) {
      slideshow.select(this.#previousSlideIndex, undefined, { animate: false });
    } else {
      slideshow.next(undefined, { animate: false });
      setTimeout(() => this.#preloadNextPreviewImage());
    }
  }

  /**
   * Resets the image to the variant image.
   * @param {PointerEvent} event - The pointer event.
   */
  resetImage(event) {
    if (event.pointerType !== 'mouse') return;

    const { slideshow } = this.refs;

    if (!this.variantPicker) {
      if (!slideshow) return;
      slideshow.previous(undefined, { animate: false });
    } else {
      this.#resetVariant();
    }
  }

  /**
   * Resets the image to the variant image.
   */
  #resetVariant = () => {
    const { slideshow, swatchOverlay } = this.refs;

    if (!slideshow) return;

    // Restore swatch overlay to the selected variant's color, or the initial default
    if (swatchOverlay) {
      const selectedLabel = this.variantPicker?.selectedOption?.closest('label');
      const selectedSwatchColor = selectedLabel?.dataset.swatchColor;

      if (selectedSwatchColor) {
        swatchOverlay.style.background = selectedSwatchColor;
      } else {
        swatchOverlay.style.background = swatchOverlay.dataset.defaultColor || '';
      }
    }

    // If we have a selected variant, always use its image
    if (this.variantPicker?.selectedOption) {
      const id = this.variantPicker.selectedOption.dataset.optionMediaId;
      if (id) {
        slideshow.select({ id }, undefined, { animate: false });
        return;
      }
    }

    // No variant selected - use initial slide if it's valid
    const initialSlide = slideshow.initialSlide;
    const slideId = initialSlide?.getAttribute('slide-id');
    if (initialSlide && slideshow.slides?.includes(initialSlide) && slideId) {
      slideshow.select({ id: slideId }, undefined, { animate: false });
      return;
    }

    // No valid initial slide or selected variant - go to previous
    slideshow.previous(undefined, { animate: false });
  };

  /**
   * Intercepts the click event on the product card anchor, we want
   * to use this to add an intermediate state to the history.
   * This intermediate state captures the page we were on so that we
   * navigate back to the same page when the user navigates back.
   * In addition to that, it captures the product card anchor so that we
   * have the specific product card in view.
   *
   * A product card can have other interactive elements like variant picker,
   * so we do not navigate if the click was on one of those elements.
   *
   * @param {Event} event
   */
  navigateToProduct = (event) => {
    if (!(event.target instanceof Element)) return;

    // Don't navigate if this product card is marked as no-navigation (e.g., in theme editor)
    if (this.hasAttribute('data-no-navigation')) return;

    // Includes swatches-variant-picker-component so a click anywhere in the swatch row (incl. a
    // bare swatch <span> on the 2nd click of a double-click) never triggers JS card navigation.
    const interactiveElement = event.target.closest(
      'button, input, label, select, [tabindex="1"], swatches-variant-picker-component'
    );

    // If the click was on an interactive element, do nothing.
    if (interactiveElement) {
      return;
    }

    const link = this.refs.productCardLink;
    if (!link.href) return;
    const linkURL = new URL(link.href);

    const productCardAnchor = link.getAttribute('id');
    if (!productCardAnchor) return;

    const infiniteResultsList = this.closest('results-list[infinite-scroll="true"]');
    if (!window.Shopify.designMode && infiniteResultsList) {
      const url = new URL(window.location.href);
      const parent = this.closest('li');
      url.hash = '';
      if (parent && parent.dataset.page) {
        url.searchParams.set('page', parent.dataset.page);
      }

      // State kept and no fragment to jump to: the scroll anchor restores the position on Back.
      yieldToMainThread().then(() => {
        history.replaceState(history.state, '', url.toString());
      });
    }

    const targetLink = event.target.closest('a');
    // Let the native navigation handle the click if it was on a link.
    if (!targetLink) {
      this.#navigateToURL(event, linkURL);
    }
  };

  /**
   * Resets the variant.
   */
  resetVariant = debounce(this.#resetVariant, 100);

  /**
   * Syncs the swatch overlay background with the currently selected variant's swatch color.
   * Called after variant selection (#handleVariantUpdate) to keep the overlay in sync.
   */
  #updateSwatchOverlay() {
    const { swatchOverlay } = this.refs;

    if (!swatchOverlay) return;

    const selectedLabel = this.variantPicker?.selectedOption?.closest('label');
    const swatchColor = selectedLabel?.dataset.swatchColor;

    if (swatchColor) {
      swatchOverlay.style.background = swatchColor;
      swatchOverlay.dataset.defaultColor = swatchColor;
    }
  }
}

if (!customElements.get('product-card')) {
  customElements.define('product-card', ProductCard);
}

/**
 * A custom element that displays a variant picker with swatches.
 * @typedef {import('@theme/variant-picker').VariantPickerRefs & {overflowList: HTMLElement}} SwatchesRefs
 */

/**
 * @extends {VariantPicker<SwatchesRefs>}
 */
class SwatchesVariantPickerComponent extends VariantPicker {
  connectedCallback() {
    super.connectedCallback();
    this.addEventListener('mousedown', this.#focusWithoutScroll);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('mousedown', this.#focusWithoutScroll);
  }

  /**
   * Focuses a clicked swatch's radio WITHOUT the browser's default focus scroll.
   *
   * Card swatch rows live inside horizontal snap carousels (the PDP suggested-products
   * `resource-list__carousel` is `scroll-snap-type: x mandatory` with `scroll-behavior: smooth`).
   * Clicking a label natively focuses its radio, and that default focus scroll ABORTS any snap
   * settle still in flight — the carousel then re-snaps from wherever it was, which after a fresh
   * swipe is back to the START. Measured mid-glide: control settles at the 720px target;
   * `focus()` drags it back to 0; `focus({ preventScroll: true })` settles at 720.
   *
   * `preventDefault()` on mousedown suppresses only the native focus (the click still fires and
   * checks the radio); focus is then restored by hand with preventScroll so keyboard users and
   * :focus-visible behaviour are unaffected — Tab navigation never passes through here.
   * @param {MouseEvent} event
   */
  #focusWithoutScroll = (event) => {
    if (!(event.target instanceof Element)) return;
    const label = event.target.closest('label.variant-option__button-label');
    if (!label || !this.contains(label)) return;
    event.preventDefault();
    const input = label.querySelector('input[type="radio"]');
    if (input instanceof HTMLInputElement) input.focus({ preventScroll: true });
  };

  /**
   * Handles card swatch changes.
   * @param {Event} event - The variant change event.
   */
  variantChanged(event) {
    if (!(event.target instanceof HTMLElement)) return;

    // Check if this is a swatch input
    const isSwatchInput = event.target instanceof HTMLInputElement && event.target.name?.includes('-swatch');
    const clickedSwatch = event.target;
    const hasAvailableVariant = clickedSwatch.dataset.hasAvailableVariant === 'true';
    const firstAvailableVariantId = clickedSwatch.dataset.firstAvailableOrFirstVariantId;

    // Request the first available variant for this swatch.
    if (isSwatchInput && hasAvailableVariant && firstAvailableVariantId) {
      event.stopPropagation();
      this.updateSelectedOption(clickedSwatch);

      // Start the colour-carousel slide now, as production does in its swatch change handler
      // (`productImageSwiper.slideTo`), instead of waiting for the section re-render below.
      const card = this.closest('product-card');
      if (card instanceof ProductCard) card.animateToVariantImage(clickedSwatch.dataset.optionMediaId);

      const optionValueId = clickedSwatch.dataset.optionValueId || '';
      const connectedProductUrl = clickedSwatch.dataset.connectedProductUrl || '';
      const requestUrl = this.buildRequestUrl(clickedSwatch, 'product-card', [optionValueId]);

      this.fetchUpdatedSection(requestUrl, {
        detail: { optionValueId, variantId: firstAvailableVariantId, connectedProductUrl },
      });
      return;
    }

    // For all other cases, use the default behavior
    super.variantChanged(event);
  }

  /**
   * Shows all swatches.
   * @param {Event} [event] - The event that triggered the show all swatches.
   */
  showAllSwatches(event) {
    event?.preventDefault();

    const { overflowList } = this.refs;

    if (overflowList instanceof OverflowList) {
      overflowList.showAll();
    }
  }
}

if (!customElements.get('swatches-variant-picker-component')) {
  customElements.define('swatches-variant-picker-component', SwatchesVariantPickerComponent);
}
